import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { useAuth } from "@/contexts/AuthContext";
import type { DBOfferIdeaCard } from "@/lib/db-types";

/** Feltene klienten kan sette på et Idé-kort. */
export type IdeaCardPatch = Partial<
  Pick<
    DBOfferIdeaCard,
    | "kind" | "body" | "url" | "storage_path" | "vimeo_id" | "source_type" | "source_id"
    | "source_label" | "source_date" | "x" | "y" | "width" | "group_id" | "use_for_ai"
  >
>;

const cardsKey = (offerId: string) => ["offer-idea-cards", offerId];

/** Kortene på Idé-tavlen (VIB-2980), uten mykt slettede. */
export function useOfferIdeaCards(offerId: string | undefined) {
  return useQuery({
    queryKey: ["offer-idea-cards", offerId],
    enabled: !!offerId,
    queryFn: async (): Promise<DBOfferIdeaCard[]> => {
      const { data, error } = await supabase
        .from("offer_idea_cards")
        .select("*")
        .eq("offer_id", offerId!)
        .is("deleted_at", null)
        .order("created_at");
      if (error) throw error;
      return (data ?? []) as DBOfferIdeaCard[];
    },
  });
}

const fail = (what: string) => (err: unknown) =>
  toast.error(`Kunne ikke ${what}: ${err instanceof Error ? err.message : "ukjent feil"}`);

/**
 * Mutasjoner for kortene på ett tilbud. Aktør er den ekte innloggede
 * brukeren, også under impersonering. Flytting og redigering oppdaterer
 * cachen med en gang, så kortet ikke hopper tilbake mens lagringen går.
 */
export function useOfferIdeaCardMutations(offerId: string) {
  const qc = useQueryClient();
  const { user: authUser } = useAuth();
  const actor = authUser?.id ?? null;
  const refresh = () => invalidateFor(qc, "offer_idea_cards", { offerId });

  const patchCache = (changes: { id: string; patch: IdeaCardPatch }[]) =>
    qc.setQueryData<DBOfferIdeaCard[]>(cardsKey(offerId), (old) =>
      old?.map((c) => {
        const hit = changes.find((ch) => ch.id === c.id);
        // Ny updated_at med en gang, så kortet som nettopp ble flyttet legges øverst.
        return hit ? { ...c, ...hit.patch, updated_at: new Date().toISOString() } : c;
      }),
    );

  const create = useMutation({
    mutationFn: async (fields: IdeaCardPatch & Pick<DBOfferIdeaCard, "kind">): Promise<DBOfferIdeaCard> => {
      const { data, error } = await supabase
        .from("offer_idea_cards")
        .insert({ ...fields, offer_id: offerId, created_by: actor, updated_by: actor })
        .select()
        .single();
      if (error) throw error;
      return data as DBOfferIdeaCard;
    },
    onSuccess: refresh,
    onError: fail("lage kortet"),
  });

  /** Oppdaterer ett eller flere kort (flere når en gruppe flyttes). */
  const update = useMutation({
    mutationFn: async (changes: { id: string; patch: IdeaCardPatch }[]) => {
      const results = await Promise.all(
        changes.map(({ id, patch }) =>
          supabase.from("offer_idea_cards").update({ ...patch, updated_by: actor }).eq("id", id),
        ),
      );
      const error = results.find((r) => r.error)?.error;
      if (error) throw error;
    },
    onMutate: patchCache,
    onSettled: refresh,
    onError: fail("lagre kortet"),
  });

  /** Myk sletting og angre: `deleted` true setter deleted_at, false fjerner den. */
  const setDeleted = useMutation({
    mutationFn: async ({ ids, deleted }: { ids: string[]; deleted: boolean }) => {
      if (ids.length === 0) return;
      const { error } = await supabase
        .from("offer_idea_cards")
        .update({ deleted_at: deleted ? new Date().toISOString() : null, updated_by: actor })
        .in("id", ids);
      if (error) throw error;
    },
    onMutate: ({ ids, deleted }) => {
      if (deleted) {
        qc.setQueryData<DBOfferIdeaCard[]>(cardsKey(offerId), (old) => old?.filter((c) => !ids.includes(c.id)));
      }
    },
    onSettled: refresh,
    onError: fail("slette kortet"),
  });

  return { create, update, setDeleted };
}
